import { MODES } from '@aasis21/weft-shared';
import type {
  ApprovalRequestMsg,
  DeviceSnapshotMsg,
  DeviceUtilityCode,
  KeepAwakeStatusMsg,
  ElicitationRequestMsg,
  HistoryItem,
  ListenerProject,
  LogLineMsg,
  PromptAttachment,
  PromptDelivery,
  SessionMode,
  SpawnMode,
  SessionOffer,
  SessionFolder,
  StoredSession,
  TransportDescriptor,
} from '@aasis21/weft-shared';
import type { RegisteredDevice } from '@/lib/devices';

export type SessionStatus = 'initializing' | 'connecting' | 'live' | 'idle' | 'ended' | 'error';

export type ToolStatus = 'running' | 'success' | 'error';

export interface UserItem {
  kind: 'user';
  id: string;
  text: string;
  ts: number;
  failed?: boolean;
  delivery?: PromptDelivery;
  origin?: 'phone' | 'terminal';
  /** This phone prompt already absorbed a laptop echo, so it can't absorb another. */
  echoed?: boolean;
  attachments?: PromptAttachment[];
}

export interface AssistantItem {
  kind: 'assistant';
  id: string;
  text: string;
  ts: number;
}

export interface ToolItem {
  kind: 'tool';
  id: string;
  name: string;
  args?: unknown;
  status: ToolStatus;
  resultPreview?: string;
  startedAt: number;
  finishedAt?: number;
  ts: number;
}

export interface NoticeItem {
  kind: 'notice';
  id: string;
  level: LogLineMsg['level'];
  text: string;
  ts: number;
}

export type TimelineItem = UserItem | AssistantItem | ToolItem | NoticeItem;

export type { ApprovalRequestMsg, ElicitationRequestMsg, HistoryItem, PromptAttachment, SessionMode };

export interface DebugEvent {
  id: string;
  dir: 'in' | 'out';
  eventType: string;
  eventSubtype: string;
  senderName: string;
  ts: number;
  msg: unknown;
}

export interface ChannelHistoryEntry {
  channelId: string;
  startedAt: number;
  endedAt?: number;
}

export interface DeviceMonitoringState {
  monitorId: string;
  startedAt: number;
  latestSequence: number;
  snapshot?: DeviceSnapshotMsg;
  error?: string;
}

export interface DeviceClipboardState {
  requestId?: string;
  operation?: 'read' | 'write';
  pending: boolean;
  text?: string;
  code?: DeviceUtilityCode;
}

export interface DeviceKeepAwakeState {
  requestId?: string;
  pending: boolean;
  status?: KeepAwakeStatusMsg;
  code?: DeviceUtilityCode;
}

export interface SessionMeta {
  channelId: string;
  sessionId?: string;
  title: string;
  /** Most recent real title reported by Copilot. Kept separately while a phone-local label is active
   *  so an unchanged heartbeat preserves that label but a laptop `/rename` can still take ownership. */
  reportedTitle?: string;
  /** True once the user has renamed this session on the phone. An unchanged CLI title does not
   *  override it; a newly reported CLI title clears the local override. */
  renamed?: boolean;
  /** Transport channels this durable session has rotated through (a `copilot --resume` mints a new
   *  channelId). The current transport is `channelId`; superseded ones are archived here so the debug
   *  Dev-detail tab can show "this session reconnected N times" (#154). */
  channelHistory?: ChannelHistoryEntry[];
  cwd: string | null;
  kind: 'live' | 'demo' | 'spawning';
  addedAt: number;
  scannedAt?: number;
  /** Stable, non-secret `deviceId` (see extension/src/deviceIdentity.mjs) of the listener that
   *  spawned this session via "Start session", if any — lets the Device details screen list every
   *  session launched from a given laptop even across `weft start` restarts. Undefined for
   *  sessions joined by scanning a session QR directly. */
  spawnedFromDeviceId?: string;
  /** Display name of the spawning device at spawn time (falls back label if it's since renamed). */
  spawnedFromDeviceName?: string;
  /** Which transport kind this session is currently paired over (local/supabase/devtunnel)
   *  — see TransportDescriptor in shared/transport.d.ts. Surfaced in the session debug/detail view
   *  so a `/weft <transport>` switch (or a devtunnel pairing) is visible from the phone side too.
   *  Optional: sessions restored before this field existed simply omit it until their next re-pair. */
  transport?: TransportDescriptor['kind'];
  /** The paired laptop's Weft version at pairing time (from the QR/pairing payload). Optional —
   *  older laptops omit it, and sessions paired before this field existed omit it until re-paired.
   *  Surfaced on the phone's Settings page so you can see which extension build you're driving. */
  laptopVersion?: string;
}

export interface ListenerDeviceState extends RegisteredDevice {
  projects: ListenerProject[];
  /** Laptop-configured initial permission selection for Start/Resume. */
  defaultPermissionMode?: SpawnMode;
  /** Behaviors advertised by the current Device Station. Runtime-only and refreshed with PROJECT_LIST. */
  capabilities?: string[];
  projectsLoading: boolean;
  connected: boolean;
  error?: string;
  /** Current page-scoped telemetry lease and newest accepted snapshot. Runtime-only. */
  monitoring?: DeviceMonitoringState;
  /** Exists only while the explicit clipboard sheet is open. Never persist or log text. */
  clipboard?: DeviceClipboardState;
  /** Authoritative station lease; closing its sheet does not stop it. Never persisted. */
  keepAwake?: DeviceKeepAwakeState;
  /** In-session `/weft` sessions this laptop is currently offering for one-tap adoption (the mirror
   *  of the "Start session" spawn flow). Each carries the offered session's own pairing payload, so
   *  tapping it pairs digitally — no QR scan. The station relays this list (SESSION_OFFERS) on bind
   *  and whenever it changes; adopting one sends SESSION_CLAIMED back so it's dropped. Runtime-only
   *  (not persisted): offers are ephemeral and re-advertised on the next reconnect. */
  offers?: SessionOffer[];
  /** Past CLI sessions from this laptop's session store, pulled on-demand (SESSION_LIST_REQUEST →
   *  SESSION_LIST) when the Device details "Resume a session" section mounts or is refreshed. Newest
   *  first, capped at SESSION_LIST_MAX, already filtered to sessions whose cwd still exists. Tapping
   *  one spawns `copilot --resume=<id>` and pairs like the "Start session" flow. Runtime-only (not
   *  persisted) — the store is high-churn, so it's always re-pulled rather than cached across runs. */
  sessions?: StoredSession[];
  /** Per-folder session totals across the WHOLE laptop store, sent alongside SESSION_LIST. Kept apart
   *  from `sessions` because that is only the newest page (and may be scoped to one folder), so
   *  counting it would understate every folder that fell off the end. Runtime-only. */
  sessionFolders?: SessionFolder[];
  /** True while a SESSION_LIST_REQUEST is in flight, so the section can show a spinner. Runtime-only. */
  sessionsLoading?: boolean;
  /** Epoch ms of the most recent attempt to (re)connect to this laptop — stamped when a connect
   *  attempt begins, independent of whether it succeeds. Paired with `lastSeenAt` (last SUCCESSFUL
   *  contact) it lets the sidebar say "last seen 2h ago · tried 5s ago" so a wedged/offline laptop
   *  reads honestly instead of a bare "Offline". Runtime-only (not persisted); resets each app run. */
  lastAttemptAt?: number;
  /** Raw wire events exchanged over the DEVICE (listener) channel — project list request/reply,
   *  spawn request/pairing/result, forget — oldest-first (DebugPanel renders them newest-first).
   *  Excludes DEVICE_HEARTBEAT (liveness is already surfaced via `connected`/`lastSeenAt`). Not
   *  persisted across app restarts; the channel itself is re-established on reconnect. */
  events: DebugEvent[];
}

export interface SessionConnection {
  status: SessionStatus;
  busy: boolean;
  busyFrom: number | null;
  /** The agent's own one-line description of what it is doing right now, or null when it hasn't
   *  said or the turn has ended. Live-only by construction: the SDK marks these events ephemeral and
   *  never writes them to the session log, so this can never be recovered on reconnect and must
   *  never be treated as history. */
  intent: string | null;
  /** When the agent started its current thinking block, stamped on THIS device's clock at the moment
   *  the message arrived, or null when it isn't thinking. Timing is deliberately local: the laptop
   *  sends only the fact, so a clock skew between the two devices can't produce a nonsense duration. */
  thinkingSince: number | null;
  mode: SessionMode;
  pendingMode?: SessionMode;
  reconnecting: boolean;
  settling: boolean;
  /** True once the session was evicted from the warm pool (subscription/socket dropped) and has no
   *  live client. Distinguishes cold-idle ("Offline — tap to connect") from warm-idle ("Quiet",
   *  socket still open) so the header and reconnect affordance stop contradicting each other. */
  cold: boolean;
  lastHeartbeat: number | null;
  ended: boolean;
  endedReason?: string;
  error?: string;
  spawning?: {
    requestId: string;
    deviceId: string;
    deviceName?: string;
    projectName: string;
    operation: 'new' | 'resume';
    stage: 'not-delivered' | 'delivered' | 'launched' | 'ready' | 'pairing' | 'pairing-failed';
    createdAt: number;
    /** The permission mode the attempt was made with. Recorded because it is a different axis from
     *  `connection.mode` (interactive/plan/autopilot) and is otherwise unrecoverable — a retry that
     *  silently dropped "allow all" would quietly re-arm approval prompts the user had opted out of. */
    mode?: SpawnMode;
    /** Set once a resume has run past the point a fresh spawn would have landed. Purely a note —
     *  the session is still on its way — so the header can stop reading as a hang. */
    slow?: boolean;
  };
}

export interface SessionHistory {
  items: HistoryItem[];
  cursor: number | null;
  hasMore: boolean;
  loading: boolean;
  latestTurnIndex: number | null;
}

export interface SessionRequests {
  approvals: ApprovalRequestMsg[];
  approvalErrors: Record<string, string>;
  elicitations: ElicitationRequestMsg[];
  elicitationErrors: Record<string, string>;
}

export interface Session {
  workspaceCapabilities?: string[];
  id: string;
  meta: SessionMeta;
  unread: boolean;
  /** Number of unread host turns/events accrued while this session was NOT active. Reset to 0 on
   *  select; summed on merge. `unread` stays as the coarse boolean mirror (`unreadCount > 0`). */
  unreadCount: number;
  lastEventAt: number | null;
  /** User-pinned (#163): exempt from warm-pool eviction preference AND the 2-day auto-delete sweep.
   *  Hydrated from the persisted `StoredSession.pinned`; never auto-cleared. */
  pinned: boolean;
  transcript: { items: TimelineItem[] };
  history: SessionHistory;
  connection: SessionConnection;
  requests: SessionRequests;
  debug: DebugEvent[];
}

const DEFAULT_MODE = MODES[0] as SessionMode;

export function emptySession(id: string, meta: SessionMeta): Session {
  return {
    id,
    meta: { ...meta },
    unread: false,
    unreadCount: 0,
    lastEventAt: null,
    pinned: false,
    transcript: { items: [] },
    history: { items: [], cursor: null, hasMore: false, loading: false, latestTurnIndex: null },
    connection: {
      status: 'idle',
      busy: false,
      busyFrom: null,
      intent: null,
      thinkingSince: null,
      mode: DEFAULT_MODE,
      reconnecting: false,
      settling: false,
      cold: false,
      lastHeartbeat: null,
      ended: false,
    },
    requests: { approvals: [], approvalErrors: {}, elicitations: [], elicitationErrors: {} },
    debug: [],
  };
}
